/**
 * <dropit-footer> — the playful bits of the DROPIT footer.
 *
 * - The :D symbol's eyes follow the pointer (only while the footer is on screen) and blink now and then.
 * - The giant logotype rises into place the first time it scrolls into view.
 *
 * Everything degrades to a static footer: no JS, touch-only devices and reduced motion all keep the
 * final state.
 */
class DropitFooter extends HTMLElement {
  #frame = 0;
  #blinkTimer = null;
  #visible = false;

  connectedCallback() {
    this.eyes = this.querySelector('[data-eyes]');
    this.face = this.querySelector('[data-face]');
    this.logo = this.querySelector('[data-logo]');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.target === this) {
            this.#visible = entry.isIntersecting;
            this.#visible ? this.#scheduleBlink() : clearTimeout(this.#blinkTimer);
          }
          if (entry.target === this.logo && entry.isIntersecting) {
            this.logo.setAttribute('data-revealed', '');
            this.observer.unobserve(this.logo);
          }
        }
      },
      { threshold: 0.15 }
    );
    this.observer.observe(this);
    if (this.logo) this.observer.observe(this.logo);

    if (reduced) {
      this.logo?.setAttribute('data-revealed', '');
      return;
    }

    if (this.eyes && this.face && window.matchMedia('(hover: hover)').matches) {
      this.onPointerMove = (event) => {
        if (!this.#visible) return;
        cancelAnimationFrame(this.#frame);
        this.#frame = requestAnimationFrame(() => this.#look(event.clientX, event.clientY));
      };
      window.addEventListener('pointermove', this.onPointerMove, { passive: true });
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    clearTimeout(this.#blinkTimer);
    if (this.onPointerMove) window.removeEventListener('pointermove', this.onPointerMove);
  }

  #look(x, y) {
    const box = this.face.getBoundingClientRect();
    const dx = x - (box.left + box.width / 2);
    const dy = y - (box.top + box.height * 0.15);
    const distance = Math.hypot(dx, dy) || 1;
    const reach = Math.min(1, distance / 400);
    // in SVG units of the 86 × 113 symbol
    const moveX = (dx / distance) * 6 * reach;
    const moveY = (dy / distance) * 5 * reach;
    this.eyes.style.setProperty('--look-x', `${moveX.toFixed(2)}px`);
    this.eyes.style.setProperty('--look-y', `${moveY.toFixed(2)}px`);
  }

  #scheduleBlink() {
    clearTimeout(this.#blinkTimer);
    if (!this.eyes) return;
    this.#blinkTimer = setTimeout(() => {
      this.eyes.setAttribute('data-blink', '');
      setTimeout(() => this.eyes.removeAttribute('data-blink'), 160);
      if (this.#visible) this.#scheduleBlink();
    }, 2500 + Math.random() * 3500);
  }
}

if (!customElements.get('dropit-footer')) customElements.define('dropit-footer', DropitFooter);
